import React, { useEffect, useState } from "react";
import { Clock, Eye, Receipt } from "lucide-react";
import { formatShortDate, timeElapsed, parseMDYDateTime } from "../utils/date";
import OrderChatDrawer from "../../common/OrderChatDrawer";

const statusClasses = (status) => {
  const s = (status || "").toLowerCase();
  const base =
    "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold";

  if (s.includes("waiting")) return `${base} bg-rose-50 text-rose-700`;
  if (s.includes("upi")) return `${base} bg-purple-50 text-purple-700`;
  if (s.includes("credit")) return `${base} bg-blue-50 text-blue-700`;
  if (s.includes("cash")) return `${base} bg-amber-50 text-amber-700`;
  if (s.includes("unpaid")) return `${base} bg-rose-50 text-rose-700`;
  if (s.includes("paid")) return `${base} bg-emerald-50 text-emerald-700`;

  if (["completed", "delivered"].includes(s))
    return `${base} bg-blue-50 text-blue-700`;

  if (["rejected", "refunded", "not-delivered"].includes(s))
    return `${base} bg-red-50 text-red-700`;

  return `${base} bg-gray-100 text-gray-700`;
};

const typeClasses = (type) => {
  const t = (type || "").toLowerCase();
  const base =
    "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold";

  if (t.includes("home")) return `${base} bg-emerald-50 text-emerald-700`;
  if (t.includes("take")) return `${base} bg-amber-50 text-amber-700`;
  if (t.includes("dini")) return `${base} bg-indigo-50 text-indigo-700`;
  if (t.includes("car")) return `${base} bg-fuchsia-50 text-fuchsia-700`;
  if (t.includes("office")) return `${base} bg-sky-50 text-sky-700`;
  return `${base} bg-gray-100 text-gray-700`;
};

const formatOrderType = (type) => {
  const t = (type || "").toLowerCase();

  if (t.includes("home")) return "H.D";
  if (t.includes("take")) return "T.A";
  if (t.includes("dini")) return "D.O";
  if (t.includes("car")) return "C.D";
  if (t.includes("office")) return "O.F";

  return type || "-";
};

const formatPaymentStatus = (row) => {
  const status = (row.payment_status || "").toLowerCase();
  const mode = (row.payment_mode || "").toLowerCase();
  const isDining = row.orderType?.toLowerCase().includes("dining");
  const tableNo = row.table_no || "-";

  let text = "Paid";

  if (status === "unpaid") text = "Waiting";
  else if (status === "credit" || mode.includes("credit"))
    text = "Credit";
  else if (status === "paid" && mode.includes("upi"))
    text = "UPI";
  else if (status === "paid" && mode.includes("cash")) text = "Cash";

  const badge = statusClasses(text.toLowerCase());

  return isDining ? (
    <span className={badge}>
      Table No: {tableNo} / {text}
    </span>
  ) : (
    <span className={badge}>{text}</span>
  );
};

// Check if order is "new" (last N minutes)
const isNewOrder = (orderDate, minutesWindow = 5) => {
  if (!orderDate) return false;

  const d = new Date(orderDate);
  if (Number.isNaN(d.getTime())) return false;

  const diffMs = Date.now() - d.getTime();
  const diffMinutes = diffMs / (1000 * 60);

  return diffMinutes <= minutesWindow;
};

const OrdersTable = ({ rows = [], loading, onView, onDelay, onEasebuzzStatusClick, page = 1, pageSize = 10 }) => {
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");

  // Only used to trigger re-render for timeElapsed
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Auto-refresh time elapsed every 30 seconds
    const id = setInterval(() => {
      setNow(Date.now());
    }, 30000);

    return () => clearInterval(id);
  }, []);

  const start = (page - 1) * pageSize;
  const slice = rows.slice(start, start + pageSize);

  const isNewOrder = (dateStr, windowMinutes = 5) => {
    const dt = parseMDYDateTime(dateStr);
    if (!dt) return false;

    const diffMs = Date.now() - dt.getTime();
    const diffMinutes = diffMs / (1000 * 60);

    return diffMinutes <= windowMinutes;
  };

  return (
    <div className="overflow-auto">
      <table className="w-full min-w-[960px] text-left text-sm">
        <thead className="bg-emerald-50 text-xs font-semibold text-gray-700">
          <tr>
            <th className="px-3 py-2">Client</th>
            <th className="px-3 py-2">D/ID</th>
            <th className="px-3 py-2">ORD/ID</th>
            <th className="px-3 py-2">SR/NO</th>
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Time Elapsed</th>
            <th className="px-3 py-2">Name & Mobile No.</th>
            <th className="px-3 py-2">Order Type</th>
            <th className="px-3 py-2">Delay</th>
            <th className="px-3 py-2">Payment Status</th>
            <th className="px-3 py-2">Action</th>
          </tr>
        </thead>

        <tbody>
          {slice.map((row) => (
            <tr
              key={`${row.orderid}-${row.id}`}
              className="border-b last:border-0 hover:bg-gray-50/50"
              style={{
                backgroundColor:
                  row.delayIndicator && row.delayIndicator === "Y"
                    ? "#FECACA"
                    : ""
              }}
            >
              {/* Client + NEW Badge */}
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <div
                    className="max-w-[220px] truncate"
                    title={row.organization_name || row.us_code}
                  >
                    {row.organization_name || row.us_code}
                  </div>

                  {isNewOrder(row.order_date) && (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      NEW
                    </span>
                  )}
                </div>

                <div className="text-[11px] text-gray-500">{row.us_code}</div>
              </td>

              {/* Order ID */} <td className="px-3 py-2 font-semibold text-gray-900">
                {row.daily_order_number}
              </td>
              <td className="px-3 py-2 font-semibold text-gray-900" onClick={() => {
                setSelectedOrderId(row?.order_id || row?.orderid);
                setChatDrawerOpen(true);
              }} style={{ cursor: "pointer" }} title="Click to view order chat">
                {row.orderid}
              </td>

              <td className="px-3 py-2 font-semibold text-gray-900">
                {row.srNo}
              </td>

              {/* Date */}
              <td className="px-3 py-2">{formatShortDate(row.order_date)}</td>

              {/* Time Elapsed */}
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                  <Clock size={12} className="text-gray-500" />
                  {timeElapsed(row.order_date, now)}
                </span>
              </td>

              {/* Customer Name & Mobile */}
              <td className="px-3 py-2">
                <div className="font-medium">{row.customer_name || "-"}</div>
                <div className="text-[11px] text-gray-600">
                  {row.customer_mobile || "-"}
                </div>
              </td>

              {/* Order Type */}
              <td className="px-3 py-2">
                <span className={typeClasses(row.orderType)}>
                  {formatOrderType(row.orderType)}
                </span>
              </td>
              <td className="px-3 py-2">
                {row.delayIndicator && row.delayIndicator === "Y" ? (
                  <button
                    type="button"
                    onClick={() => onDelay && onDelay(row)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100"
                    title="View delay timeline"
                  >
                    Delay
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
                    ON TIME
                  </span>
                )}
              </td>


              {/* Payment Status */}
              <td className="px-3 py-2">{formatPaymentStatus(row)}</td>

              {/* Action */}
              <td className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => onView && onView(row)}
                    className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs hover:bg-gray-50"
                    title="View / Payment Details"
                  >
                    <Eye size={14} /> View
                  </button>
                  <button
                    onClick={() => onEasebuzzStatusClick && onEasebuzzStatusClick(row)}
                    className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs hover:bg-gray-50"
                    title="Check Easebuzz Transaction"
                  >
                    <Receipt size={14} /> Status
                  </button>
                  {!!(row?.txnId || row?.txnid || row?.transactionId) && (
                    <button
                      onClick={() => onEasebuzzStatusClick && onEasebuzzStatusClick(row)}
                      className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-600"
                      title="Check Easebuzz Transaction"
                    >
                      Payment Status
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}

          {!loading && slice.length === 0 && (
            <tr>
              <td
                colSpan={11}
                className="px-3 py-10 text-center text-sm text-gray-500"
              >
                No data found.
              </td>
            </tr>
          )}

          {loading && (
            <tr>
              <td
                colSpan={8}
                className="px-3 py-10 text-center text-sm text-gray-500"
              >
                Loading…
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <OrderChatDrawer
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        orderId={selectedOrderId}
      />
    </div >
  );
};

export default OrdersTable;

